import type { ErrorCode } from '@sb-protogen/external/external_control';
import {
  ClearableBy,
  Severity,
  type Fault,
} from '@sb-protogen/external/external_control';
import type { TCErrorCode } from '@sb-protogen/messenger/trajectory_controller_service';

import { faultDocsUrl, faultMessage } from './faultCatalog';
import { REACHABLE_TC_FAULT_CODES, tcErrorCode } from './tcErrorCodes';

/** The fields a caller owns rather than the mapping. */
export interface FaultContext {
  /** Unix epoch microseconds. 0 when the caller has not stamped it. */
  occurredAtUs?: number;
  /** Repeats of this same code collapsed into this record. */
  suppressedCount?: number;
}

/**
 * Assemble a `Fault` from a code plus its derived severity and clearability.
 *
 * Every mapper in this library ends here, so `message`, `docs_url` and `field`
 * are filled identically for every code regardless of which signal produced it.
 */
export function buildFault(
  code: ErrorCode,
  severity: Severity,
  clearableBy: ClearableBy,
  context: FaultContext = {},
): Fault {
  return {
    code,
    severity,
    clearableBy,
    // Always empty on this surface. The structured detail behind a
    // trajectory-controller fault lives in the controller's debug path and never
    // leaves it, so no fault here is field-scoped.
    field: '',
    message: faultMessage(code),
    docsUrl: faultDocsUrl(code),
    occurredAtUs: context.occurredAtUs ?? 0,
    suppressedCount: context.suppressedCount ?? 0,
  };
}

/**
 * A trajectory-controller code arriving on the **fault** path.
 *
 * Every reachable code is `FAULTED`, never `REJECTED`. Every one of them
 * arrives through the controller's `enterError()`, which clears the queue and
 * fast-stops, so **motion has already stopped** by the time a client sees it.
 * Labelling e.g. `TRAJECTORY_OVER_LIMITS` as `REJECTED` ("nothing moved") would
 * be factually wrong, and a client reading that as "resend, the arm is fine"
 * would stream into an arm sitting in ERROR.
 *
 * All of them are `CLEARABLE_BY_CLIENT`: the client clears them by ending its
 * session and starting a new one, with nobody at the robot.
 */
export function mapTcFault(code: TCErrorCode, context?: FaultContext): Fault {
  return buildFault(
    tcErrorCode(code),
    Severity.FAULTED,
    ClearableBy.CLEARABLE_BY_CLIENT,
    context,
  );
}

/** True for the codes that can actually reach the fault path (`REACHABLE_TC_FAULT_CODES`). */
export function isReachableTcFaultCode(code: TCErrorCode): boolean {
  return REACHABLE_TC_FAULT_CODES.includes(code);
}
